import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormControl, Validators } from '@angular/forms'
import { dateValidator } from '@ftr/forms'
import { DestroySubscribers } from '@ftr/foundation'
import moment from 'moment-timezone'
import { Observable, distinctUntilChanged, skip, takeUntil, tap } from 'rxjs'
import { ComplexFilterData } from '../ComplexFilter'
import { DateRadioListFilter, DateRadioListOptions } from '../DateRadioListFilter'
import { ComplexFilterService } from '../complex-filter.service'

@Component({
  selector: 'ftr-complex-filter-date-radio-list',
  templateUrl: './complex-filter-date-radio-list.component.html',
  styleUrls: ['./complex-filter-date-radio-list.component.css'],
})
export class ComplexFilterDateRadioListComponent extends DestroySubscribers implements OnInit {
  /**
   * Filter configuration, must be specific to the radio-list filter type
   */
  @Input() filter: DateRadioListFilter
  @Input() visibilityToggled: Observable<boolean>
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()

  readonly dateFormat = 'YYYY-MM-DD'
  readonly dateRadioListOptions = DateRadioListOptions

  internalFormControl = new UntypedFormControl()
  dateControl = new UntypedFormControl(moment(), [Validators.required, dateValidator])
  dateRangeFromControl = new UntypedFormControl(moment(), [Validators.required, dateValidator])
  dateRangeToControl = new UntypedFormControl(moment(), [Validators.required, dateValidator])

  private lastValue: ComplexFilterData

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    if (!this.filter.options || !this.filter.options.length) {
      throw new Error('No options provided for the date radio list filter')
    }

    this.buildInternalFormControls()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  listenToFilterChange(): void {
    this.complexFilterService
      .listenToFilterChange([this.filter])
      .pipe(takeUntil(this.finalize))
      .subscribe(filters => {
        const [filter, dateFrom, dateTo] = filters[this.filter.filterKey] ?? ([] as string[])

        // Patch the controls without emitting
        this.internalFormControl.patchValue(filter, { emitEvent: false })

        this.lastValue = this.internalFormControl.value

        if (filter === DateRadioListOptions.SelectDate) {
          this.dateControl.patchValue(moment(dateFrom), { emitEvent: false })
        }

        if (filter === DateRadioListOptions.SelectDateRange) {
          this.dateRangeFromControl.patchValue(moment(dateFrom), { emitEvent: false })
          this.dateRangeToControl.patchValue(moment(dateTo), { emitEvent: false })
        }
      })
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   * Still works when applied immediately, because lastValue is updated.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          if (!visible) {
            this.internalFormControl.reset(this.lastValue, { emitEvent: false })
          }
        }),
      )
      .subscribe()
  }

  private buildInternalFormControls(): void {
    this.internalFormControl.valueChanges.pipe(takeUntil(this.finalize)).subscribe(filterKey => {
      switch (filterKey) {
        case DateRadioListOptions.SelectDate:
          this.filterChanged.emit({
            [this.filter.filterKey]: [DateRadioListOptions.SelectDate, this.getFormattedDate(this.dateControl.value)],
          })
          break
        case DateRadioListOptions.SelectDateRange:
          this.filterChanged.emit({
            [this.filter.filterKey]: [
              DateRadioListOptions.SelectDateRange,
              this.getFormattedDate(this.dateRangeFromControl.value),
              this.getFormattedDate(this.dateRangeToControl.value),
            ],
          })
          break
        default:
          this.filterChanged.emit({ [this.filter.filterKey]: filterKey })
      }
    })

    this.dateControl.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(date => {
        if (this.internalFormControl.value !== DateRadioListOptions.SelectDate) {
          return
        }

        this.filterChanged.emit({
          [this.filter.filterKey]: [DateRadioListOptions.SelectDate, this.getFormattedDate(date)],
        })
      })

    this.dateRangeFromControl.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(dateFrom => {
        this.filterChanged.emit({
          [this.filter.filterKey]: [
            DateRadioListOptions.SelectDateRange,
            this.getFormattedDate(dateFrom),
            this.getFormattedDate(this.dateRangeToControl.value),
          ],
        })
      })

    this.dateRangeToControl.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(dateTo => {
        this.filterChanged.emit({
          [this.filter.filterKey]: [
            DateRadioListOptions.SelectDateRange,
            this.getFormattedDate(this.dateRangeFromControl.value),
            this.getFormattedDate(dateTo),
          ],
        })
      })
  }

  private getFormattedDate(value: string | moment.Moment): string {
    return (moment.isMoment(value) ? value : moment(value)).format(this.dateFormat)
  }
}

function momentDateComparator(one: moment.Moment, two: moment.Moment): boolean {
  return moment.isMoment(one) && moment.isMoment(two) && one.isSame(two)
}
